import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Profile, ProfilePayload, Platform } from '@/types/profile';
import * as policiesApi from '@/services/policiesApi';
import * as tagsApi from '@/services/tagsApi';

import { PolicyDetails, CreatePolicyRequest } from '@/types/api';
import { toast } from 'sonner';
import { inferProfileIconFromPolicy } from '@/utils/profileIcons';

const POLICIES_KEY = ['policies'];

/**
 * Map of Apple payload identifiers (e.g. "com.apple.security.firewall")
 * to their supported platforms from the local payload registry.
 * This allows inferring platforms from API payload data.
 */
const APPLE_IDENTIFIER_PLATFORM_MAP: Record<string, Platform[]> = {
  // Security
  'com.apple.security.firewall': ['macos'],
  'com.apple.SoftwareUpdate': ['macos'],
  'com.apple.systempolicy.control': ['macos'],
  'com.apple.MCX': ['macos'],
  'com.apple.MCX.FileVault2': ['macos'],
  'com.apple.applicationaccess': ['macos', 'ios', 'ipados', 'tvos'],
  'com.apple.screensaver': ['macos'],
  'com.apple.loginwindow': ['macos'],
  'com.apple.TCC.configuration-profile-policy': ['macos'],
  // Network
  'com.apple.wifi.managed': ['macos', 'ios', 'ipados', 'tvos'],
  'com.apple.vpn.managed': ['macos', 'ios', 'ipados'],
  'com.apple.proxy.http.global': ['macos', 'ios', 'ipados'],
  'com.apple.cellularprivatenetwork.managed': ['ios'],
  // General
  'com.apple.dock': ['macos'],
  'com.apple.finder': ['macos'],
  'com.apple.desktop': ['macos'],
  'com.apple.notificationsettings': ['macos', 'ios', 'ipados'],
  'com.apple.ManagedClient.preferences': ['macos'],
  // Accounts
  'com.apple.mail.managed': ['macos', 'ios', 'ipados'],
  'com.apple.caldav.account': ['macos', 'ios', 'ipados'],
  'com.apple.carddav.account': ['macos', 'ios', 'ipados'],
  'com.apple.eas.account': ['ios', 'ipados'],
  'com.apple.ldap.account': ['macos', 'ios', 'ipados'],
  // Media
  'com.apple.airplay': ['macos', 'ios', 'ipados', 'tvos'],
  'com.apple.font': ['macos', 'ios', 'ipados'],
  // Certificates
  'com.apple.security.pkcs1': ['macos', 'ios', 'ipados', 'tvos'],
  'com.apple.security.scep': ['macos', 'ios', 'ipados', 'tvos'],
};

const normalizePlatforms = (value: unknown): Platform[] => {
  const allowed = new Set<Platform>(['macos', 'ios', 'ipados', 'tvos']);
  return Array.isArray(value)
    ? value
        .map((platform) => typeof platform === 'string' ? platform.toLowerCase() : '')
        .filter((platform): platform is Platform => allowed.has(platform as Platform))
    : [];
};

function policyToProfile(p: PolicyDetails): Profile {
  // Map real payloads if available from the API response
  const mappedPayloads: ProfilePayload[] = (p.payloads || []).map((pl) => ({
    payloadId: pl.kind || pl.identifier || pl.policyPayloadId || '',
    configValues: pl.configJson ? (typeof pl.configJson === 'string' ? (() => { try { return JSON.parse(pl.configJson); } catch { return {}; } })() : pl.configJson as Record<string, unknown>) : {},
  }));

  // If the list endpoint only returns payloadCount but not the full payloads array,
  // create placeholder entries so the count displays correctly on cards
  const finalPayloads = mappedPayloads.length > 0
    ? mappedPayloads
    : Array.from({ length: p.payloadCount || 0 }, (_, i) => ({
        payloadId: `payload-${i}`,
        configValues: {},
      }));

  const tenantId = (p as any).tenantId || (p as any).tenant_id || undefined;

  // Infer platforms from payload identifiers using the Apple identifier map
  const inferredPlatforms: Platform[] = [...new Set(
    (p.payloads || [])
      .flatMap((pl) => {
        const identifier = pl.identifier || pl.kind || '';
        return APPLE_IDENTIFIER_PLATFORM_MAP[identifier] || [];
      })
  )];
  const explicitPlatforms = normalizePlatforms((p as any).platforms);

  const payloadIdentifiers = (p.payloads || []).map((pl) => pl.identifier || pl.kind || '');
  const icon = inferProfileIconFromPolicy(p.name, payloadIdentifiers);

  return {
    id: p.id,
    name: p.name,
    description: p.description || '',
    payloads: finalPayloads,
    platforms: explicitPlatforms.length > 0 ? explicitPlatforms : inferredPlatforms,
    customer: tenantId,
    icon,
    createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
    updatedAt: p.updatedAt ? new Date(p.updatedAt) : new Date(),
    isDraft: false,
  };
}

export const useProfiles = () => {
  const queryClient = useQueryClient();

  const { data: profiles = [], isLoading: loading, error } = useQuery({
    queryKey: POLICIES_KEY,
    queryFn: async () => {
      const policies = await policiesApi.listPolicies();
      const list = Array.isArray(policies) ? policies : [];
      const profiles = list.map(policyToProfile);

      // Enrich with tenant IDs inferred from assigned tags
      const enriched = await Promise.all(
        profiles.map(async (profile) => {
          try {
            const tags = await tagsApi.getTagsByPolicy(profile.id);
            const tenantIds = [...new Set(
              tags
                .map((t: any) => t.tenantId || t.tenant_id)
                .filter(Boolean)
            )];
            return {
              ...profile,
              customer: tenantIds[0] || profile.customer,
              customers: tenantIds.length > 1 ? tenantIds : undefined,
            };
          } catch {
            return profile;
          }
        })
      );
      return enriched;
    },
  });

  const addMutation = useMutation({
    mutationFn: async (req: CreatePolicyRequest) => {
      return policiesApi.createPolicy(req);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: POLICIES_KEY });
      toast.success('Configuration profile created successfully');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to create profile');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return policiesApi.deletePolicy(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: POLICIES_KEY });
      toast.success('Profile deleted');
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Failed to delete profile');
    },
  });

  const addProfile = useCallback(
    (profile: Omit<Profile, 'id' | 'createdAt' | 'updatedAt'>, _isDraft = false) => {
      const req: CreatePolicyRequest = {
        name: profile.name,
        description: profile.description,
        payloads: profile.payloads.map((p) => ({
          kind: p.payloadId,
          identifier: p.payloadId,
          config: p.configValues,
        })),
      };
      addMutation.mutate(req);
      // Return a temporary profile object for compatibility
      return {
        ...profile,
        id: `temp-${Date.now()}`,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as Profile;
    },
    [addMutation]
  );

  const updateProfile = useCallback(
    (id: string, profileData: Partial<Profile>) => {
      // For now, re-create via update API
      const req: CreatePolicyRequest = {
        name: profileData.name || '',
        description: profileData.description,
        payloads: (profileData.payloads || []).map((p) => ({
          kind: p.payloadId,
          identifier: p.payloadId,
          config: p.configValues,
        })),
      };
      policiesApi.updatePolicy(id, req).then(() => {
        queryClient.invalidateQueries({ queryKey: POLICIES_KEY });
        toast.success('Profile updated successfully');
      }).catch((err) => {
        toast.error(err.message || 'Failed to update profile');
      });
    },
    [queryClient]
  );

  const deleteProfile = useCallback(
    (id: string) => {
      // Return the promise so callers can await completion (and surface errors).
      return deleteMutation.mutateAsync(id);
    },
    [deleteMutation]
  );

  return {
    profiles,
    loading,
    error: error as Error | null,
    addProfile,
    updateProfile,
    deleteProfile,
  };
};
